import { useContext, useEffect, useRef, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NameContext } from "../context/NameContext";
import KompassOverview from "../kits/ManifestKit/KompassOverview";
import { HashLink } from "react-router-hash-link";
import { motion } from "framer-motion";
import { loadModelViewer } from "../utils/loadModelViewer";
import SeoHelmet from "../components/SeoHelmet";
import MonoButton from "../kits/DesignKit/MonoButton";
import { FaRocket, FaCode, FaChalkboardTeacher, FaComments } from "react-icons/fa";
import TechStack, { SectionBadge } from "./TechStack";
import { PaperBG, PaperGradientBG } from "../kits";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import rough from "roughjs/bundled/rough.esm.js";

import "@fontsource/poppins/400.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "@fontsource/poppins/800.css";


// Kunden-Logos mit Bildern (gleiche wie Testimonials)
const trustedCompanies = [
  { name: "adidas", image: "/images/alogo.webp" },
  { name: "Volvo", image: "/images/volvo.svg" },
  { name: "Stadt Hamburg", image: "/images/hh.svg" },
  { name: "BASF", image: "https://upload.wikimedia.org/wikipedia/commons/9/9c/BASF-Logo_bw.svg", large: true },
  { name: "Siemens", image: "https://upload.wikimedia.org/wikipedia/commons/7/79/Siemens_Healthineers_logo.svg", large: true },
  { name: "Telekom", image: "https://upload.wikimedia.org/wikipedia/commons/d/dd/Deutsche_Telekom_2022.svg" },
  { name: "Otto Group", image: "https://upload.wikimedia.org/wikipedia/commons/1/11/Otto_Group_Logo_2022.svg", large: true },
  { name: "Hapag-Lloyd", image: "https://upload.wikimedia.org/wikipedia/commons/6/6a/Hapag-Lloyd.svg", large: true },
  { name: "Bayer", image: "https://upload.wikimedia.org/wikipedia/commons/f/f7/Logo_Bayer.svg" },
];

// Handgezeichneter Gold-Kreis
function RoughCircle({ size = 56, color = "#AC8E66", strokeWidth = 1.5, roughness = 1.5, className = "" }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}

function Hero({ className = "" }) {
  const {
    name,
    setName,
    setIsNameEntered,
  } = useContext(NameContext);
  const navigate = useNavigate();

  useEffect(() => {
    loadModelViewer();
  }, []);

  const kompassRef = useRef(null);
  const pillarDevRef = useRef(null);
  const pillarTrainingRef = useRef(null);
  const pillarBeratungRef = useRef(null);
  const statsProjectsRef = useRef(null);
  const statsTrainingsRef = useRef(null);
  const statsYearsRef = useRef(null);

  return (
    <>
      <SeoHelmet
        title="Denis Bitter – Software Architect & Full-Stack Engineering Instructor"
        description="React · Angular · Flutter · UI Engineering · System Architecture. Ich entwickle klare Systeme für Lernen, Web & App – durchdacht, echt, skalierbar. EdTech. Medien. Lösungen mit Haltung."
        url="https://www.denisbitter.de/"
        image="https://www.denisbitter.de/images/hero-og.jpg"
      />

      {/* ========== AKT 1: MEIN WARUM ========== */}
      <section
        className={`relative overflow-hidden px-4 font-mono ${className}`}
      >
        <PaperBG />

        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center pt-24 sm:pt-28 pb-16">

          {/* Announcement Badge */}
    

          {/* Kernaussage */}
          <h1 className="mt-10 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            Ich löse Probleme,
            <span className="block mt-1 text-brand drop-shadow-[0_0_12px_rgba(215,172,116,0.35)]">
              indem ich entwickle.
            </span>
          </h1>

          <p className="mt-6 text-sm sm:text-base text-slate-700 max-w-2xl leading-relaxed">
            Nicht Code um des Codes willen. Sondern Systeme, die im Alltag funktionieren –
            und von Menschen verstanden werden.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <MonoButton
              onClick={() => {
                navigate('/about#referenzen');
              }}
              className="px-6 py-2 text-sm bg-transparent
               border-brand w-full
               hover:bg-slate-800
               sm:w-auto cursor-pointer"
              textColor="text-brand"
              underlineColors="ZenBrand"
            >
              Projekte ansehen
            </MonoButton>

         
       
          </div>

        </div>
      </section>

      {/* ========== 1. WAS MICH ANTREIBT ========== */}
      <PaperGradientBG variante = "zenBlack"
      >
        <section className="relative px-4 py-16 font-mono text-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-brand uppercase tracking-wider text-center mb-6">Was mich antreibt</p>
            <div className="grid sm:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <p className="text-sm text-amber-100 font-medium">Klarheit statt Komplexität</p>
                <p className="text-xs text-brand mt-1">Systeme, die jeder versteht</p>
              </div>
              <div className="p-4">
                <p className="text-sm text-amber-100 font-medium">Befähigen statt Abliefern</p>
                <p className="text-xs text-brand mt-1">Teams, die selbst weiterkommen</p>
              </div>
              <div className="p-4">
                <p className="text-sm text-amber-100 font-medium">Langfristig denken</p>
                <p className="text-xs text-brand mt-1">Lösungen, die Bestand haben</p>
              </div>
            </div>
          </div>
        </section>
      </PaperGradientBG>

      {/* ========== 2. SOFTWARE DIE FUNKTIONIERT ========== */}
      <section className="relative px-4 py-16 font-mono">
        <PaperBG />
        <div className="relative z-10 max-w-4xl mx-auto">

          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              Software, Training + Beratung die funktioniert <br/>
              <span className="text-brand"> UND </span>
              verstanden wird.
            </h2>
          </div>

          {/* Drei Säulen */}
          <div className="grid sm:grid-cols-3 gap-6">

            {/* Entwicklung */}
            <div ref={pillarDevRef} className="relative">
              <div className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
              rounded-lg p-6
              text-center shadow-md hover:shadow-sm transition-shadow h-full">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-transparent border border-brand flex items-center justify-center">
                  <FaCode className="text-brand text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-brand mb-2">Entwicklung</h3>
                <p className="text-xs text-slate-800 leading-relaxed">
                  React · Angular · Flutter · System Architecture
                </p>
                <p className="text-xs text-brand mt-3">
                  Von der Idee zur skalierbaren Lösung
                </p>
              </div>
              <RoughBorder
                parentRef={pillarDevRef}
                radius={10}
                padding={2}
                color="#AC8E66"
                strokeWidth={0.3}
                roughness={1}
                bowing={2}
              />
            </div>

            {/* Training */}
            <div ref={pillarTrainingRef} className="relative">
              <div className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
              rounded-lg p-6
              text-center shadow-md hover:shadow-sm transition-shadow h-full">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-transparent border border-brand flex items-center justify-center">
                  <FaChalkboardTeacher className="text-brand text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-brand mb-2">Training</h3>
                <p className="text-xs text-slate-800 leading-relaxed">
                  Workshops · Kurse · Team-Enablement
                </p>
                <p className="text-xs text-brand mt-3">
                  Wissen, das im Alltag wirkt
                </p>
              </div>
              <RoughBorder
                parentRef={pillarTrainingRef}
                radius={10}
                padding={2}
                color="#AC8E66"
                strokeWidth={0.3}
                roughness={1}
                bowing={2}
              />
            </div>

            {/* Beratung */}
            <div ref={pillarBeratungRef} className="relative">
              <div className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
              rounded-lg p-6
              text-center shadow-md hover:shadow-sm transition-shadow h-full">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-transparent border border-brand flex items-center justify-center">
                  <FaComments className="text-brand text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-brand mb-2">Beratung</h3>
                <p className="text-xs text-slate-800 leading-relaxed">
                  Architektur-Reviews · Code-Audits · Strategie
                </p>
                <p className="text-xs text-brand mt-3">
                  Klarheit für komplexe Entscheidungen
                </p>
              </div>
              <RoughBorder
                parentRef={pillarBeratungRef}
                radius={10}
                padding={2}
                color="#AC8E66"
                strokeWidth={0.3}
                roughness={1}
                bowing={2}
              />
            </div>

          </div>

        </div>
      </section>

      {/* ========== 3. TECH STACK ========== */}
      <section className="relative px-4 py-16 font-mono">
        <PaperBG />
        <div className="relative z-10 max-w-4xl mx-auto">
          <TechStack showBadge extended />
        </div>
      </section>

      {/* ========== 4. VERTRAUEN VON ========== */}
      <section className="relative px-4 py-16 font-mono">
        <PaperBG />
        <div className="relative z-10 max-w-4xl mx-auto mb-5">
          <div className="text-center">
            <SectionBadge label="Vertrauen von" />
            <div className="flex flex-wrap justify-center gap-4 sm:gap-5 mt-10">
              {trustedCompanies.map((company) => (
                <div
                  key={company.name}
                  onClick={() => {
                    navigate('/about');
                    const scrollToRef = () => {
                      const el = document.getElementById('referenzen');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } else {
                        setTimeout(scrollToRef, 50);
                      }
                    };
                    setTimeout(scrollToRef, 50);
                  }}
                  className="relative w-[72px] h-[72px] sm:w-[80px] sm:h-[80px]
                  flex items-center justify-center
                  group cursor-pointer
                  bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] rounded-full
                  "
                  title={company.name}
                >
                  {/* Mobile circle */}
                  <RoughCircle size={72} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="sm:hidden" />
                  {/* Desktop circle */}
                  <RoughCircle size={80} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="hidden sm:block" />
                  <img
                    src={company.image}
                    alt={company.name}
                    className={`object-contain opacity-60 group-hover:opacity-100 transition-opacity ${
                      company.large ? "w-12 h-12 sm:w-14 sm:h-14" : "w-7 h-7 sm:w-8 sm:h-8"
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== 5. ZAHLEN & PRINZIPIEN ========== */}
      <PaperGradientBG >
        <section className="relative px-4 py-16 font-mono text-white">
          <div className="max-w-4xl mx-auto">

            {/* Zahlen */}

            <div className="flex flex-wrap 
            justify-center gap-8 sm:gap-16 
            mb-10">
              <div
                ref={statsProjectsRef}
                className="relative text-center 
                rounded-full w-28 h-28 sm:w-32 sm:h-32 
                flex flex-col items-center justify-center 
                shadow-sm shadow-slate-900 border-[1px] border-brand/80 
                bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]"
              >
                <span className="block text-3xl sm:text-4xl font-bold text-brand">50+</span>
                <span className="text-xs text-slate-900 uppercase tracking-wider">Projekte</span>
                <RoughBorder
                  parentRef={statsProjectsRef}
                  radius={999}
                  padding={3}
                  color="#AC8E66"
                  strokeWidth={0.6}
                  roughness={1.2}
                  bowing={1.2}
                />
              </div>
              <div
                ref={statsTrainingsRef}
                className="relative text-center rounded-full w-28 h-28 sm:w-32 sm:h-32 
                flex flex-col items-center 
                justify-center 
               shadow-sm shadow-slate-900 border-[1px] border-brand/80 
                bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]"
              >
                <span className="block text-3xl sm:text-4xl font-bold text-brand">500+</span>
                <span className="text-xs text-slate-900 uppercase tracking-wider">Trainings</span>
                <RoughBorder
                  parentRef={statsTrainingsRef}
                  radius={999}
                  padding={3}
                  color="#AC8E66"
                  strokeWidth={0.9}
                  roughness={1.2}
                  bowing={1.21}
                />
              </div>
              <div
                ref={statsYearsRef}
                className="relative text-center rounded-full 
                w-28 h-28 sm:w-32 sm:h-32 flex flex-col 
                items-center justify-center 
                shadow-sm shadow-slate-900 border-[1px] border-brand/80 
                bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]"
              >
                <span className="block text-3xl sm:text-4xl font-bold text-brand">10+</span>
                <span className="text-xs text-slate-900 uppercase tracking-wider">Jahre</span>
                <RoughBorder
                  parentRef={statsYearsRef}
                  radius={999}
                  padding={3}
                  color="#AC8E66"
                  strokeWidth={0.6}
                  roughness={1.2}
                  bowing={1.2}
                />
              </div>
            </div>
       

            {/* Meine Prinzipien */}
            <div className="">
              <div className="text-center mb-8">
                <p className="text-xs text-brand uppercase tracking-wider mb-2">Meine Prinzipien</p>
                <p className="text-sm text-amber-100">
                  Was mich leitet – in jedem Projekt, in jeder Zusammenarbeit.
                </p>
              </div>
            {/* Foto/Avatar */}
            <div
              onClick={() => {
                navigate('/about');
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
              }}
              className="relative w-32 h-32 md:w-40 md:h-40 mx-auto mb-8 block cursor-pointer group"
              aria-label="Zu About"
            >
              {/* RoughCircle für Mobile */}
              {[0, 1].map((delay) => (
                <motion.span
                  key={`halo-${delay}`}
                  className="absolute inset-0 rounded-full border border-brand/90 pointer-events-none"
                  initial={{ opacity: 0.4, scale: 0.95 }}
                  animate={{ opacity: 0, scale: 1.4 }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatType: "loop", delay: delay * 0.5, ease: "easeInOut" }}
                />
              ))}
              <div className="absolute inset-0 flex items-center justify-center md:hidden">
                <RoughCircle size={128} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              {/* RoughCircle für Desktop */}
              <div className="absolute inset-0 items-center justify-center hidden md:flex">
                <RoughCircle size={160} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-brand to-brand flex items-center justify-center overflow-hidden">
                <img
                  src="/images/den.png"
                  width={256}
                  height={256}
                  alt="Denis Bitter"
                  className="w-full h-full object-cover object-[80%_20%]"
                />
              </div>
            </div>


              <HashLink
                smooth
                to="/about#kompass"
                scroll={(el) => el.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="block w-full max-w-4xl mx-auto"
              >
                <div
                  ref={kompassRef}
                  className="relative p-3 sm:p-4 hover:opacity-90 transition-opacity"
                >
                  <KompassOverview className="w-full" />
                </div>
              </HashLink>
            </div>
            

          </div>
       
        </section>
      </PaperGradientBG>
    </>
  );

}

export default Hero;
